import { shrinkPhoto } from '../../api/shrink';

/**
 * Las fotos de la gama de Phelon & Moore: se suben sin fondo (PNG transparente) y la landing las
 * encuadra sola al publicar (phelon-moore-web, scripts/content-fetch.mjs → frameFor). Aquí está la
 * misma cuenta, para que el editor muestre cómo va a quedar: si cambia allá, cambia aquí.
 *
 * Lienzo transparente de 1400 × 966; la moto (lo que tiene una opacidad de 80 % o más) ocupa de
 * x = 45 a 1355 (1311 px) o, si es alta, de y = 6 a 898, centrada a lo ancho y apoyada en la línea
 * y = 899. Una foto que ya mide 1400 × 966 se usa tal cual. Una sin transparencia (un JPG) se
 * recorta a lo que no es el fondo liso de las esquinas y queda como un recuadro.
 */
export const FRAME = { w: 1400, h: 966, left: 45, right: 1356, top: 6, ground: 899, solid: 204, trim: 12 } as const;

export interface Framing {
  /** Medidas de la foto tal como se ve. */
  width: number;
  height: number;
  /** Ya mide 1400 × 966: la landing la usa tal cual. */
  asIs: boolean;
  /** No tiene transparencia: se verá como un recuadro sobre el gris de la web. */
  opaque: boolean;
  /** Dónde queda la foto entera en el lienzo, en fracciones del lienzo (left/top/width/height en % para CSS). */
  left: number;
  top: number;
  w: number;
  h: number;
  /** La moto, en píxeles de la foto. */
  box: { x: number; y: number; w: number; h: number };
  /** Escala con que entra la foto: más de 1 = se amplía (y se ve blanda). */
  scale: number;
}

const loadImage = (url: string) =>
  new Promise<HTMLImageElement>((resolve, reject) => {
    const img = new Image();
    img.decoding = 'async';
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error('foto'));
    img.src = url;
  });

/** Lee la foto (ya subida) y calcula dónde la pone la landing. */
export async function frameOf(url: string): Promise<Framing> {
  const img = await loadImage(url);
  const W = img.naturalWidth;
  const H = img.naturalHeight;
  if (W === FRAME.w && H === FRAME.h) {
    return { width: W, height: H, asIs: true, opaque: false, left: 0, top: 0, w: 1, h: 1, box: { x: 0, y: 0, w: W, h: H }, scale: 1 };
  }
  // Los píxeles se leen a 1200 px de lado como mucho: para la vista previa sobra y es rápido.
  const k = Math.min(1, 1200 / Math.max(W, H));
  const cw = Math.max(1, Math.round(W * k));
  const ch = Math.max(1, Math.round(H * k));
  const canvas = document.createElement('canvas');
  canvas.width = cw;
  canvas.height = ch;
  const ctx = canvas.getContext('2d', { willReadFrequently: true });
  if (!ctx) throw new Error('canvas');
  ctx.drawImage(img, 0, 0, cw, ch);
  const { data } = ctx.getImageData(0, 0, cw, ch);

  let x0 = cw;
  let y0 = ch;
  let x1 = -1;
  let y1 = -1;
  let opaque = true;
  const grow = (x: number, y: number) => {
    if (x < x0) x0 = x;
    if (x > x1) x1 = x;
    if (y < y0) y0 = y;
    if (y > y1) y1 = y;
  };
  for (let y = 0; y < ch; y++) {
    for (let x = 0; x < cw; x++) {
      const a = data[(y * cw + x) * 4 + 3]!;
      if (a < 255) opaque = false;
      if (a >= FRAME.solid) grow(x, y);
    }
  }
  if (opaque) {
    // Sin transparencia: lo que se distingue del color de la esquina (como el recorte de la landing).
    x0 = cw;
    y0 = ch;
    x1 = -1;
    y1 = -1;
    const [r0, g0, b0] = [data[0]!, data[1]!, data[2]!];
    for (let y = 0; y < ch; y++) {
      for (let x = 0; x < cw; x++) {
        const i = (y * cw + x) * 4;
        if (Math.max(Math.abs(data[i]! - r0), Math.abs(data[i + 1]! - g0), Math.abs(data[i + 2]! - b0)) > FRAME.trim) grow(x, y);
      }
    }
  }
  if (x1 < 0) {
    // transparente entera, o lisa entera: la foto completa
    x0 = 0;
    y0 = 0;
    x1 = cw - 1;
    y1 = ch - 1;
  }
  const box = { x: x0 / k, y: y0 / k, w: (x1 + 1 - x0) / k, h: (y1 + 1 - y0) / k };
  const scale = Math.min((FRAME.right - FRAME.left) / box.w, (FRAME.ground - FRAME.top) / box.h);
  const ox = FRAME.left + (FRAME.right - FRAME.left - box.w * scale) / 2 - box.x * scale;
  const oy = FRAME.ground - (box.y + box.h) * scale;
  return { width: W, height: H, asIs: false, opaque, left: ox / FRAME.w, top: oy / FRAME.h, w: (W * scale) / FRAME.w, h: (H * scale) / FRAME.h, box, scale };
}

/** left/top/width/height de la foto dentro de un lienzo de 1400 × 966 (CSS, en %). */
export const framedStyle = (f: Framing) => ({
  left: `${(f.left * 100).toFixed(3)}%`,
  top: `${(f.top * 100).toFixed(3)}%`,
  width: `${(f.w * 100).toFixed(3)}%`,
  height: `${(f.h * 100).toFixed(3)}%`,
});

const MAX_SIDE = 2400;
/** La API acepta fotos de hasta 15 MB. */
const MAX_BYTES = 14 * 1024 * 1024;

function canvas(width: number, height: number): HTMLCanvasElement {
  const c = document.createElement('canvas');
  c.width = width;
  c.height = height;
  return c;
}

/**
 * Prepara una foto sin fondo para subirla: si mide más de 2400 px de lado (o pesa demasiado), la
 * achica sin perder la transparencia (en PNG). Un JPG va por el camino de las demás fotos
 * (shrinkPhoto): no tiene transparencia que cuidar.
 */
export async function shrinkCutout(file: File): Promise<File> {
  if (file.type === 'image/jpeg' || /\.jpe?g$/i.test(file.name)) return shrinkPhoto(file);
  let bitmap: ImageBitmap;
  try {
    bitmap = await createImageBitmap(file);
  } catch {
    return file;
  }
  try {
    const { width, height } = bitmap;
    const scale = Math.min(1, MAX_SIDE / Math.max(width, height));
    if (scale === 1 && file.size <= MAX_BYTES) return file;
    const tw = Math.max(1, Math.round(width * scale));
    const th = Math.max(1, Math.round(height * scale));
    // De a mitades: de un solo salto la foto sale dentada.
    let src: CanvasImageSource = bitmap;
    let w = width;
    let h = height;
    while (w / 2 >= tw) {
      const step = canvas(Math.round(w / 2), Math.round(h / 2));
      const ctx = step.getContext('2d')!;
      ctx.imageSmoothingQuality = 'high';
      ctx.drawImage(src, 0, 0, step.width, step.height);
      src = step;
      w = step.width;
      h = step.height;
    }
    const out = canvas(tw, th);
    const ctx = out.getContext('2d')!;
    ctx.imageSmoothingQuality = 'high';
    ctx.drawImage(src, 0, 0, tw, th);
    const blob = await new Promise<Blob | null>((resolve) => out.toBlob(resolve, 'image/png'));
    if (!blob || blob.type !== 'image/png') return file;
    return new File([blob], `${file.name.replace(/\.[^.]+$/, '') || 'moto'}.png`, { type: 'image/png', lastModified: Date.now() });
  } catch {
    return file;
  } finally {
    bitmap.close();
  }
}
